import { useGetTenant, useListDeployments, useListWorkspaces } from '@sunstone/api-client/hooks';
import { useAuthToken } from '@/features/auth';
import { type RuleDraft, ruleDraftConditionJson, ruleDraftHasContent } from './accessRuleDraft';

/**
 * Resolves a deployment/workspace id to its display name, scoped to one
 * tenant. Moved out of UserDetailPage.tsx so the invite wizard's Confirm
 * step and the member-detail edit modal's Confirm step can render the same
 * rule sentences the member-detail Access rules tab already does.
 */
export function useResourceNames(tenantId: string) {
  const { token } = useAuthToken();
  const { data: deploymentsData } = useListDeployments(token, tenantId, undefined, {
    pageSize: 200,
  });
  const { data: workspacesData } = useListWorkspaces(undefined, token, { tenantId, pageSize: 200 });

  const names = new Map<string, string>();
  for (const w of workspacesData?.workspaces ?? []) names.set(w.id, w.displayName);
  for (const d of deploymentsData?.deployments ?? []) names.set(d.id, d.displayName);

  return (id: string) => names.get(id) ?? id;
}

/** Resolves tenantId to its display name — undefined while loading or for a platform-wide (no tenantId) context. */
export function useTenantName(tenantId: string): string | undefined {
  const { token } = useAuthToken();
  const { data } = useGetTenant(tenantId, token);
  return data?.tenant?.displayName;
}

/**
 * Whether tenantId is a partner (no parent tenant) rather than a customer
 * tenant — a partner owns no deployments/workspaces directly, only its
 * child tenants do, so the resource pickers scope to it via
 * parent_tenant_id (see DeploymentPicker/WorkspacePicker's parentTenantId
 * prop) rather than tenant_id. An empty tenantId (platform-wide scope) is
 * treated as "partner" too, matching RoleAccessWizard's own isPartner
 * formula, though that case never renders "this partner" text (see
 * tenantSummaryLine, which checks tenantId first).
 */
export function useIsPartnerTenant(tenantId: string): boolean {
  const { token } = useAuthToken();
  const { data } = useGetTenant(tenantId, token);
  return tenantId ? !data?.tenant?.parentId : true;
}

export function ruleSentence(
  rule: {
    effect: string;
    resourceIds: string[];
    restrictionKind: string;
    ruleConditionJson: string;
  },
  resourceName: (id: string) => string,
): string {
  const verb = rule.effect === 'deny' ? 'Cannot access' : 'Can access';
  if (rule.restrictionKind === 'rule_based' && rule.ruleConditionJson) {
    try {
      const conditions = JSON.parse(rule.ruleConditionJson) as {
        attribute: string;
        value: string;
      }[];
      const condText = conditions.map((c) => `${c.attribute} = ${c.value}`).join(' and ');
      return `${verb} anything matching ${condText} (now and in the future).`;
    } catch {
      return `${verb} resources matching a rule-based condition.`;
    }
  }
  if (rule.restrictionKind !== 'id_based' || rule.resourceIds.length === 0) {
    return `${verb} the resources this restriction names.`;
  }
  const resourceText = rule.resourceIds.map((id) => `\`${resourceName(id)}\``).join(', ');
  return `${verb} ${resourceText}.`;
}

/**
 * Composes one kind's whole rule list into a single summary line: "Full
 * access." when there are no content-bearing rules, else every rule's
 * sentence joined in order. Deployment and Workspace rules are independent
 * lists, so this is a plain join, not a hierarchy composition — but
 * reachability itself is NOT independent: a workspace only satisfies the
 * overall restriction when it passes both this kind's own summary AND the
 * other kind's (the deployment gate is evaluated on its parent deployment,
 * the workspace gate on itself, and both must pass — see
 * accessrule.decide/gate). This function only renders one kind's line; the
 * cross-kind AND relationship is explained once, alongside both lines, by
 * AccessSummary.
 */
export function kindSummaryLine(drafts: RuleDraft[], resourceName: (id: string) => string): string {
  const content = drafts.filter(ruleDraftHasContent);
  if (content.length === 0) return 'Full access.';
  return content
    .map((draft) =>
      ruleSentence(
        {
          effect: draft.effect,
          resourceIds: draft.resourceIds,
          restrictionKind: draft.restrictionKind,
          ruleConditionJson: ruleDraftConditionJson(draft),
        },
        resourceName,
      ),
    )
    .join(' ');
}

/** The Tenants summary line: which tenant(s) the role being assigned covers — derived from route context (tenantId presence), the same signal RolePermissionSummary's scopeLabel already uses, not from access_rules (which never restrict at tenant level). isPartner distinguishes a partner tenant (whose scope actually spans all its customer tenants too, via parent_tenant_id — see DeploymentPicker's parentTenantId prop) from a plain customer tenant. */
export function tenantSummaryLine(
  tenantId: string,
  tenantName?: string,
  isPartner?: boolean,
): string {
  if (!tenantId) return 'The entire platform.';
  if (isPartner) return `This partner (${tenantName ?? tenantId}), including its customers.`;
  return `This tenant only (${tenantName ?? tenantId}).`;
}
